// client/src/features/rent/search.ts — SCR-RENT-001's URL state · RENT-003 (04 §9.2.3, §8.5).
//
// §9.2.3 is NORMATIVE for every LIST screen and §8.5 names it again for this one: "month
// navigation and status/period filters as validated search params". So the navigated month is
// URL state exactly as the status filter is — a landlord can send „the overdue rows of March"
// as a link, refresh keeps it, and Back steps the month back.
//
// `features/tenants/search.ts` is the shape this copies. The one departure is that `year` and
// `month` have NO Zod default, and that is deliberate: a `.default()` evaluated at parse time
// would bake "the month the tab was opened" into every URL the router normalises, so a link
// shared on the 1st would mean a different month to whoever opened it, and a test would depend
// on the wall clock. They are optional here and resolved once, explicitly, by
// `resolveBoardPeriod` — after which every navigation the user makes writes them out in full.

import { z } from 'zod';

import { RENT_SCHEDULE_STATUSES } from './enums';
import { currentPeriod, MAX_PERIOD_YEAR, MIN_PERIOD_YEAR, type Period } from './periods';

/** 02 §7.4 — `pageSize` default 25, max 200; the server refuses more rather than clamping. */
export const rentSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  /** 03 §8.6's `CHECK (period_year BETWEEN 2000 AND 2100)`, mirrored. */
  year: z.coerce.number().int().min(MIN_PERIOD_YEAR).max(MAX_PERIOD_YEAR).optional(),
  /** 1..12, as `period_month` is. */
  month: z.coerce.number().int().min(1).max(12).optional(),
  status: z.enum(RENT_SCHEDULE_STATUSES).optional(),
  /**
   * Doc 05 RENT-003 asks for a „per-property" schedule view. Doc 04 §8.5 specifies exactly one
   * route and no per-property screen, so the view is this filter rather than a second screen.
   * See DEVIATIONS D-391.
   */
  propertyId: z.string().uuid().optional(),
  /** SCR-CON-003's „Program chirii" tab is this board with a contract pinned (04 §8.4). */
  contractId: z.string().uuid().optional(),
  /**
   * SCR-RENT-002's overlay — 04 §9.2.3's canonical `?pay=$scheduleId`, added by PAY-003.
   *
   * It is a search param and not component state for the reason §9.2.3 gives every overlay: the
   * quick-log is a place, so it survives a refresh, it can be linked to („înregistrează plata
   * pentru scadența asta"), and Back closes it instead of leaving the board. It COMPOSES with
   * the filters — opening and closing the overlay strips only this param, which is why
   * `clearedSearch` below leaves it alone and the close handler names it explicitly.
   */
  pay: z.string().uuid().optional(),
  /**
   * „Doar scadențele restante" — the ONE param FIN-005 added to this schema, and the only one
   * it added to any list it does not own.
   *
   * SCR-DASH-001's „🔴 N chirii restante" chip has to open the rows it counted, and the board
   * had no filter that names them. It could not be `status=OVERDUE`: D-678 reserves that status
   * for a past-due period with NOTHING paid, so a period five days late with 500 of 2.000 paid
   * is PARTIALLY_PAID — and its 1.500 is in the dashboard's arrears total. A status filter would
   * have shown the landlord fewer rows than the chip promised, about the same money.
   *
   * Arrears are `daysLate > 0` with a positive outstanding (D-852), which is a predicate over
   * two columns the board already renders and `GET /api/v1/rent-schedules` does not filter on.
   * FIN-005 adds no endpoint, so the filter is applied to the rows the page returned, and the
   * board says so on screen. See DEVIATIONS D-732 for what that costs and what reverses it.
   *
   * It also drops the month: a debt is not a fact about the month you are looking at.
   */
  late: z.coerce.boolean().optional(),
  /**
   * „Doar scadențele cu plată anunțată" — the rows a tenant has declared a payment on and the
   * landlord has not answered yet.
   *
   * D-1587 put an unanswered claim on the row as a chip and D-1940 made it decide the row's
   * VERB: such a period offers „Validează plata" instead of „Încasează". That made the claim a
   * state of the row in everything but name, and the board had no filter that names it — on a
   * month with forty scadențe the four that are waiting for an answer had to be found by eye.
   *
   * It is not a `status`: a claim is a fact about a scadență, not a value of
   * `RentScheduleStatus`, and the period underneath it stays PENDING, PARTIALLY_PAID or OVERDUE
   * while the claim waits. A status filter could not have expressed it.
   *
   * `GET /api/v1/rent-schedules` does not filter on it either, so — exactly as `late` above —
   * the predicate is applied to the rows that came back, over the claims the board already reads
   * for its chips. Which is why `toBoardQuery` asks for the month in ONE page of 02 §7.4's
   * maximum while this is on: a page of 25 narrowed after the fact hides the claims that landed
   * on page 2. The board says on screen what it did. See DEVIATIONS D-2440.
   *
   * It keeps the month: a claim answers one scadență, and the scadență is of some month.
   */
  declared: z.coerce.boolean().optional(),
  /**
   * „Toate lunile" — every scadență the scope holds, newest first, instead of one month
   * (D-2841). The property's profile opens its „Chirii și încasări" tab this way, because the
   * question there is the apartment's whole rent history and a month navigator answers it one
   * page at a time; the pill beside the status filter offers the same view on the board.
   *
   * Unlike `late` and `declared` this narrowing is the SERVER's: `GET /rent-schedules` treats
   * its year and month as optional and the request simply leaves them out, bounded by `dueTo` at
   * the end of next month, so the totals are the whole set's and the pager is honest.
   */
  all: z.coerce.boolean().optional(),
});

export type RentSearch = z.infer<typeof rentSearchSchema>;

/**
 * The month the board is showing. Explicit in the URL once the user has navigated even once.
 *
 * A half-specified period (`?year=2026` with no month) resolves against `now` for the missing
 * half rather than being rejected: the schema already refused anything out of range, and a
 * 422-style dead end for a hand-edited URL would be a worse answer than a sensible month.
 */
export function resolveBoardPeriod(search: RentSearch, now: Date): Period {
  const today = currentPeriod(now);
  return {
    year: search.year ?? today.year,
    month: search.month ?? today.month,
  };
}

/**
 * Which filters are engaged — the empty state needs to know why it is empty.
 *
 * The PERIOD is deliberately not one of them. Every board view is some month, so counting the
 * month as a filter would make „nicio chirie programată" unreachable and always offer to clear
 * a filter the user never set. What „clear" means here is the status and scope filters.
 */
export function hasActiveFilters(search: RentSearch): boolean {
  return (
    search.status !== undefined ||
    search.propertyId !== undefined ||
    search.contractId !== undefined ||
    search.late === true ||
    search.declared === true
  );
}

/**
 * The overlay is not a filter, so it is not one of the above and it is not cleared below: „the
 * board has no rows for these filters" is a statement about the LIST, and an open quick-log has
 * nothing to do with it (04 §9.2.3 — „overlay params compose with filters").
 */

/**
 * Clearing filters keeps the month and returns to page 1 (page 3 of a shorter set is blank).
 * „Toate lunile" is kept as the month is: it says which stretch of time the board covers, not
 * which rows of it are shown (D-2841).
 */
export function clearedSearch(search: RentSearch): RentSearch {
  const cleared: RentSearch = { page: 1, pageSize: search.pageSize };
  if (search.year !== undefined) cleared.year = search.year;
  if (search.month !== undefined) cleared.month = search.month;
  if (search.all === true) cleared.all = true;
  return cleared;
}
